import React, { useState, useContext, useEffect, useRef } from 'react'
import styles from './styles.module.css'
import { Textarea, Input } from '../../../components/ModalInputs'
import Button from '../../../components/Button'
import Confirmation from '../../../components/Confirmation/Confirmation'
import { StudentsContext } from '../../../utils/context'
import { useDispatch, useSelector } from 'react-redux'
import {
  getStudentFiles,
  uploadStudentFile,
  downloadStudentFile,
  deleteStudentFile,
  renameStudentFile,
} from '../../../actions/students/files'

const AGENT_URL = 'http://localhost:8967'

export default function Characteristic() {
  const dispatch = useDispatch()
  const { allForms, addMode, studentTabData, updateInitialValues, studentId } = useContext(StudentsContext)

  const { values, setFieldValue, setValues } = allForms
  const { characteristic } = studentTabData || {}
  const { studentFiles } = useSelector(state => state.students.data)

  const lastSyncedRef = useRef(null)
  const fileInputRef = useRef(null)
  const downloadLinkRef = useRef(null)

  const [selectedFile, setSelectedFile] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [renameMode, setRenameMode] = useState(false)
  const [newFileName, setNewFileName] = useState('')
  const [jpegQuality, setJpegQuality] = useState(60)
  const [scanners, setScanners] = useState([])
  const [selectedScanner, setSelectedScanner] = useState(null)
  const [scanning, setScanning] = useState(false)

  const handleCharacteristicChange = (e) => {
    const { name, value } = e.target
    setFieldValue(`characteristic.${name}`, value)
  }

  useEffect(() => {
    const dataJson = JSON.stringify(characteristic)
    if (!addMode && characteristic && lastSyncedRef.current !== dataJson) {
      lastSyncedRef.current = dataJson
      setValues(prev => ({ ...prev, characteristic: { ...characteristic } }))
      if (updateInitialValues) updateInitialValues('characteristic', characteristic)
    }
  }, [characteristic, addMode, setValues, updateInitialValues])

  useEffect(() => {
    if (!addMode && studentId) dispatch(getStudentFiles(studentId))
  }, [studentId, addMode]) // eslint-disable-line

  useEffect(() => {
    fetch(`${AGENT_URL}/scanners`)
      .then(r => r.ok ? r.json() : [])
      .then(list => { setScanners(list); if (list.length > 0) setSelectedScanner(list[0].id) })
      .catch(() => {})
  }, [])

  const getFileName = (idx) => {
    const f = (studentFiles || [])[idx]
    return f?.file_name || f?.name || f || ''
  }

  const handleUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    dispatch(uploadStudentFile(studentId, fd))
    e.target.value = ''
  }

  const handleDownload = async () => {
    if (selectedFile === null) return
    const fileName = getFileName(selectedFile)
    const blob = await dispatch(downloadStudentFile(studentId, fileName))
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = downloadLinkRef.current
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const handlePreview = async () => {
    if (selectedFile === null) return
    const fileName = getFileName(selectedFile)
    const blob = await dispatch(downloadStudentFile(studentId, fileName))
    if (!blob) return
    window.open(URL.createObjectURL(blob), '_blank')
  }

  const handleDeleteConfirm = () => {
    dispatch(deleteStudentFile(studentId, getFileName(selectedFile)))
    setSelectedFile(null)
    setShowDeleteConfirm(false)
  }

  const handleRenameClick = () => {
    setNewFileName(getFileName(selectedFile))
    setRenameMode(true)
  }

  const handleRenameSave = () => {
    if (!newFileName.trim()) return
    dispatch(renameStudentFile(studentId, getFileName(selectedFile), newFileName.trim()))
    setRenameMode(false)
    setSelectedFile(null)
  }

  const handleScan = async () => {
    setScanning(true)
    try {
      const res = await fetch(`${AGENT_URL}/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: selectedScanner, dpi: 200, format: 'jpeg', quality: Number(jpegQuality) || 80 })
      })
      if (!res.ok) { alert('Ошибка сканирования'); return }
      const blob = await res.blob()
      const cd = res.headers.get('content-disposition') || ''
      const match = cd.match(/filename="?([^"]+)"?/)
      const fileName = match ? match[1] : `scan_${Date.now()}.jpg`
      const fd = new FormData()
      fd.append('file', new File([blob], fileName, { type: blob.type }))
      dispatch(uploadStudentFile(studentId, fd))
    } catch {
      alert('Агент сканера недоступен')
    } finally {
      setScanning(false)
    }
  }

  return (
    <div className={styles.tabWrapper}>
      <input ref={fileInputRef} type='file' hidden onChange={handleUpload} />
      <a ref={downloadLinkRef} hidden />

      <div className={styles.characteristicContainer}>
        <div className={styles.characteristicTextarea}>
          <Textarea
            style={{ width: '100%', minHeight: '300px' }}
            value={(values.characteristic || {}).characteristic_text || ''}
            name='characteristic_text'
            onChange={handleCharacteristicChange}
          />
        </div>

        <div className={styles.characteristicFiles}>
          <div className={styles.filePanel}>
            <div className={styles.filesList}>
              {(studentFiles || []).map((file, idx) => {
                const name = file?.file_name || file?.name || file
                return (
                  <div
                    key={name || idx}
                    onClick={() => { setSelectedFile(idx); setRenameMode(false) }}
                    className={`${styles.fileItem} ${selectedFile === idx ? styles.fileItemSelected : ''}`}
                  >
                    {name}
                  </div>
                )
              })}
            </div>
            <div className={styles.filesButtons}>
              <Button mode='bordered' onClick={() => fileInputRef.current.click()}>+ Добавить</Button>
              <Button mode='bordered' onClick={handleDownload} disabled={selectedFile === null}>Получить</Button>
              <Button mode='bordered' onClick={handlePreview} disabled={selectedFile === null}>Просмотр</Button>
              <Button mode='bordered' onClick={() => setShowDeleteConfirm(true)} disabled={selectedFile === null}>Удалить</Button>
              <Button mode='bordered' onClick={handleRenameClick} disabled={selectedFile === null}>Переименовать</Button>
            </div>
          </div>

          {renameMode && (
            <div className={styles.renameRow}>
              <Input
                label='Новое имя'
                value={newFileName}
                onChange={e => setNewFileName(e.target.value)}
                style={{ width: '260px' }}
              />
              <Button mode='bordered' onClick={handleRenameSave}>Сохранить</Button>
              <Button mode='bordered' onClick={() => setRenameMode(false)}>Отмена</Button>
            </div>
          )}

          <div className={styles.fileBottomRow}>
            <Input
              label='Качество JPEG'
              type='number'
              value={jpegQuality}
              onChange={e => setJpegQuality(e.target.value)}
              style={{ width: '60px' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: '12px' }}>Сканер</span>
              <select
                value={selectedScanner || ''}
                onChange={e => setSelectedScanner(e.target.value)}
                style={{ height: '28px', minWidth: '180px' }}
              >
                {scanners.length === 0
                  ? <option value=''>—</option>
                  : scanners.map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                }
              </select>
            </div>
            <Button mode='bordered' onClick={handleScan} disabled={scanning || scanners.length === 0}>
              {scanning ? 'Сканирование...' : '+ Сканировать'}
            </Button>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <Confirmation
          title='Вы уверены, что хотите удалить файл?'
          acceptConfirmation={handleDeleteConfirm}
          rejectConfirmation={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  )
}
